import {
  DAILY_RESET_HOUR_IST,
  FIREBASE_COLLECTION_DOC_KEY,
  FIREBASE_COLLECTION_KEY,
} from "./constants";
import { updateCollection } from "./services/firebase";

import MainBazarData from "./data/tatamatka/main_bazaar.json";
import MilanData from "./data/tatamatka/milan.json";
import RajdhaniNightData from "./data/tatamatka/rajdhani_night.json";
import TataRajdhaniData from "./data/tatamatka/tata_rajdhani.json";
import TataTimeBazarData from "./data/tatamatka/tata_time_bazaar.json";
import KalyanData from "./data/tatamatka/kalyan.json";
import BhootnathData from "./data/tatamatka/bhootnath.json";
import KBombayData from "./data/tatamatka/kolkatabombay.json";

export interface GameTimeTableUI {
  name: string;
  endTime: string;
  startTime: string;
  gamesTimeTableId?: number;
}

export interface GameObject {
  lhsValue: string;
  createdTime: string;
  rhsValue: string;
  gamesTimeTableUI?: GameTimeTableUI;
  gamesTimeTableId?: number;
  guessGameTypeId?: number;
  name: string;
  endTime: string;
  winnerValue: string;
  startTime: string;
}

export interface FatafatRecord {
  fatafatGuessId?: number;
  guessNumber: string;
  resultValue?: string;
  winnerValue: string;
  startTime: string;
  createdTime: string;
}

export interface GuessRecord {
  guessGameId?: number;
  gameName: string;
  guessNumber: string;
  winnerValue: string;
  createdTime: string;
}

export interface WeekGuessRecord {
  fromdate: string;
  todate: string;
  mondayvalue: string;
  tuesdayvalue: string;
  wednesdayvalue: string;
  thursdayvalue: string;
  fridayvalue: string;
  saturdayvalue: string;
  sundayvalue: string;
}

export interface WeekPattiRecord {
  fromdate: string;
  todate: string;
  zerovalue: string;
  onevalue: string;
  twovalue: string;
  threevalue: string;
  fourvalue: string;
  fivevalue: string;
  sixvalue: string;
  sevenvalue: string;
  eightvalue: string;
  ninevalue: string;
}
export const isDev = () =>
  !process.env.NODE_ENV || process.env.NODE_ENV === "development";

export const IST_TIME_ZONE = "Asia/Kolkata";

export const EMPTY_GUESS_WEEK = {
  mondayvalue: "",
  tuesdayvalue: "",
  wednesdayvalue: "",
  thursdayvalue: "",
  fridayvalue: "",
  saturdayvalue: "",
  sundayvalue: "",
};

export const EMPTY_PATTI_WEEK = {
  zerovalue: "",
  onevalue: "",
  twovalue: "",
  threevalue: "",
  fourvalue: "",
  fivevalue: "",
  sixvalue: "",
  sevenvalue: "",
  eightvalue: "",
  ninevalue: "",
};

const readPart = (
  parts: Intl.DateTimeFormatPart[],
  type: Intl.DateTimeFormatPartTypes
) => parts.find((part) => part.type === type)?.value || "00";

/** Calendar parts in India Standard Time, independent of the browser timezone. */
export const getIstParts = (input: Date | string = new Date()) => {
  const date = input instanceof Date ? input : new Date(input);
  const safeDate = Number.isNaN(date.getTime()) ? new Date() : date;
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: IST_TIME_ZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hourCycle: "h23",
  }).formatToParts(safeDate);

  return {
    year: Number(readPart(parts, "year")),
    month: Number(readPart(parts, "month")),
    day: Number(readPart(parts, "day")),
    hour: Number(readPart(parts, "hour")) % 24,
    minute: Number(readPart(parts, "minute")),
    second: Number(readPart(parts, "second")),
  };
};

/** yyyy-mm-dd for the instant, evaluated in IST. */
export const getIstDateKey = (input?: Date | string | { toDate?: () => Date } | null) => {
  if (!input) {
    return "";
  }
  const date =
    input instanceof Date
      ? input
      : typeof (input as { toDate?: () => Date }).toDate === "function"
        ? (input as { toDate: () => Date }).toDate()
        : new Date(input as string);
  if (Number.isNaN(date.getTime())) {
    return "";
  }
  const { year, month, day } = getIstParts(date);
  return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
};

export const shiftIstDateKey = (dateKey: string, days: number) => {
  const [year, month, day] = dateKey.split("-").map(Number);
  if (!year || !month || !day) {
    return "";
  }
  const utcNoonIst = new Date(Date.UTC(year, month - 1, day, 6, 30));
  utcNoonIst.setUTCDate(utcNoonIst.getUTCDate() + days);
  return getIstDateKey(utcNoonIst);
};

/** True from 09:00 IST onward, including 10:00, 11:00, and any later time that day. */
export const isDailyResetReached = (now: Date = new Date()) => {
  const { hour, minute } = getIstParts(now);
  return hour > DAILY_RESET_HOUR_IST || (hour === DAILY_RESET_HOUR_IST && minute >= 0);
};

/** Milliseconds until the next 09:00 IST. Used when the tab is already open. */
export const msUntilDailyReset = (now: Date = new Date()) => {
  const { year, month, day } = getIstParts(now);
  const nextResetUtc = new Date(Date.UTC(year, month - 1, day, 3, 30, 0, 0));
  if (nextResetUtc.getTime() <= now.getTime()) {
    nextResetUtc.setUTCDate(nextResetUtc.getUTCDate() + 1);
  }
  return nextResetUtc.getTime() - now.getTime();
};

export const gameDisplayName = (game: any) =>
  game?.gamesTimeTableUI?.name || game?.name || "";

export const gameIdentity = (game: any) =>
  [
    game?.createdTime || "",
    gameDisplayName(game),
    game?.gamesTimeTableUI?.startTime || game?.startTime || "",
    game?.gamesTimeTableUI?.endTime || game?.endTime || "",
    game?.guessGameTypeId ?? "",
  ].join("|");

export const withGameUI = (game: any = {}) => {
  const name = gameDisplayName(game);
  const startTime = game?.gamesTimeTableUI?.startTime || game?.startTime || "";
  const endTime = game?.gamesTimeTableUI?.endTime || game?.endTime || "";
  const gamesTimeTableId =
    game?.gamesTimeTableUI?.gamesTimeTableId ?? game?.gamesTimeTableId ?? 0;
  return {
    ...game,
    name,
    startTime,
    endTime,
    gamesTimeTableId,
    lhsValue: game?.lhsValue ?? "***",
    rhsValue: game?.rhsValue ?? "***",
    winnerValue: game?.winnerValue ?? "**",
    gamesTimeTableUI: {
      name,
      startTime,
      endTime,
      gamesTimeTableId,
    },
  };
};

export const normalizeFatafat = (item: any = {}, index = 0): FatafatRecord => ({
  ...item,
  fatafatGuessId: item?.fatafatGuessId ?? index + 1,
  guessNumber: item?.guessNumber ?? item?.resultValue ?? "***",
  resultValue: item?.resultValue ?? item?.guessNumber ?? "***",
  winnerValue: item?.winnerValue ?? "**",
  startTime: item?.startTime ?? "",
  createdTime: item?.createdTime ?? "",
});

export const normalizeMatkaDocument = (raw: any = {}) => {
  const gameTypes = Array.isArray(raw?.gameTypes)
    ? raw.gameTypes.map(withGameUI)
    : [];
  const fatafatResults = Array.isArray(raw?.fatafatResults)
    ? raw.fatafatResults.map(normalizeFatafat)
    : [];
  return {
    adminAuthUsers: [],
    topBanner: {},
    noticeBoardCard: [],
    fatafat_result_timings: [],
    gameRatesCard: [],
    jodiCharts: [],
    panelCharts: [],
    metaData: {},
    ...raw,
    gameTypes,
    fatafatResults,
    guessingTableData: Array.isArray(raw?.guessingTableData)
      ? raw.guessingTableData
      : [],
    gamesTimeTable: Array.isArray(raw?.gamesTimeTable) ? raw.gamesTimeTable : [],
    guessDayRecords: Array.isArray(raw?.guessDayRecords) ? raw.guessDayRecords : [],
    pattiRecords: Array.isArray(raw?.pattiRecords) ? raw.pattiRecords : [],
    serverInfo: {
      isServerDown: Boolean(raw?.serverInfo?.isServerDown),
    },
  };
};

export const nextNumericId = (items: any[], key: string) => {
  return (items || []).reduce((max, item) => {
    const value = Number(item?.[key]);
    return Number.isFinite(value) ? Math.max(max, value) : max;
  }, 0) + 1;
};

export function isEmpty(value: any): boolean {
  return (
    value === undefined ||
    value === null ||
    (typeof value === "object" && Object.keys(value).length === 0) ||
    (typeof value === "string" && value.trim().length === 0) ||
    (typeof value === "string" && value === " ")
  );
}

// export const getStartandEndofWeek = () => {
//   const today = new Date();
//   const startWeekDate = `${today.getFullYear()}-${today.getMonth() + 1}-${
//     today.getDate() - (today.getDay() - 1)
//   }`;
//   const endWeekDate = `${today.getFullYear()}-${today.getMonth() + 1}-${
//     today.getDate() + (7 - today.getDay())
//   }`;
//   return { startWeekDate, endWeekDate };
// };

export const getStartandEndofWeek = () => {
  const todayKey = getIstDateKey(new Date());
  const [year, month, day] = todayKey.split("-").map(Number);
  const noonIst = new Date(Date.UTC(year, month - 1, day, 6, 30));
  const weekday = new Intl.DateTimeFormat("en-US", {
    timeZone: IST_TIME_ZONE,
    weekday: "short",
  }).format(noonIst);
  const mondayOffset: Record<string, number> = {
    Mon: 0,
    Tue: 1,
    Wed: 2,
    Thu: 3,
    Fri: 4,
    Sat: 5,
    Sun: 6,
  };
  const start = new Date(noonIst);
  start.setUTCDate(start.getUTCDate() - (mondayOffset[weekday] ?? 0));
  const end = new Date(start);
  end.setUTCDate(end.getUTCDate() + 6);
  return {
    startWeekDate: getIstDateKey(start),
    endWeekDate: getIstDateKey(end),
  };
};

export const getWeekRecord = (records: any[] = [], emptyShape: any = {}) => {
  const { startWeekDate, endWeekDate } = getStartandEndofWeek();
  const found = (records || []).find(
    (item) => item?.fromdate === startWeekDate && item?.todate === endWeekDate
  );
  return {
    ...emptyShape,
    ...(found || {}),
    fromdate: startWeekDate,
    todate: endWeekDate,
  };
};

export const upsertWeekRecord = (records: any[] = [], next: any) => {
  const list = Array.isArray(records) ? [...records] : [];
  const index = list.findIndex(
    (item) => item?.fromdate === next.fromdate && item?.todate === next.todate
  );
  if (index >= 0) {
    list[index] = next;
  } else {
    list.push(next);
  }
  return list;
};

export const insertSpaces = (str: string) => {
  if (Array.isArray(str)) {
    str = str.join(" ").replace(/ +/g, "");
  }
  if (typeof str !== "string") {
    return ""; // Return an empty string or handle the error as appropriate for your use case.
  }

  const regex = /^(\w{3})(\w{2})(\w+)/;
  const modifiedString = str.replace(regex, "$1 $2 $3");
  return modifiedString;
};

export const isObjectEmpty = (obj: any) => {
  return Object.keys(obj).length === 0;
};
export const getSelectedParam = (params: any) => {
  if (params && !isObjectEmpty(params) && params.gameIndex >= 0) {
    return params.gameIndex;
  }
  return null;
};

export const fetchSchedulerStatusFcn = (triggerInputData?: any) => {
  if (Array.isArray(triggerInputData) && triggerInputData.length > 0) {
    const name = triggerInputData[0].name;
    const startTime = triggerInputData[0].startTime;
    const endTime = triggerInputData[0].endTime;
    const id = 1;
    return { name, startTime, endTime, id };
  }
  console.error("Invalid or empty games timetable data");
  return null;
};

/** Kept so older call sites keep compiling. The daily cutoff for this app is 09:00 IST. */
export const is11AMOrLater = () => isDailyResetReached();
export const convertTime12to24 = (time12h) => {
  const [time, modifier] = time12h.split(" ");

  let [hours, minutes] = time.split(":");

  if (hours === "12") {
    hours = "00";
  }

  if (modifier === "PM") {
    hours = parseInt(hours, 10) + 12;
  }

  return `${hours}:${minutes}`;
};
export const onTimeChange = (inputTime) => {
  if (inputTime && (inputTime.includes("AM") || inputTime.includes("PM"))) {
    return inputTime;
  }
  try {
    let timeSplit = inputTime.split(":");
    let hours = 0;
    let minutes = 0;
    let meridian = "AM";
    hours = timeSplit[0];
    minutes = timeSplit[1];
    if (hours > 12) {
      meridian = "PM";
      hours -= 12;
    } else if (hours < 12) {
      meridian = "AM";
      if (hours == 0 || hours == parseInt("00")) {
        hours = 12;
      }
    } else {
      meridian = "PM";
    }
    return `${hours}:${minutes} ${meridian}`;
  } catch (e) {
    console.error("util onTimeChange error", e);
  }
};
export const getOldDataByName = (gameName: string) => {
  /**
   * Tata Time Bazaar
   * Tata Rajdhani
   * Milan
   * Bhoothnath
   * Rajdhani Night
   * Main Bazaar
   */
  const dataByNameObj = {
    "Main Bazaar": MainBazarData,
    Milan: MilanData,
    "Rajdhani Night": RajdhaniNightData,
    "Tata Rajdhani": TataRajdhaniData,
    "Tata Time Bazaar": TataTimeBazarData,
    Kalyan: KalyanData,
    Bhoothnath: BhootnathData,
    Kbombay: KBombayData,
  };
  if (Object.keys(dataByNameObj).includes(gameName)) {
    return dataByNameObj[gameName];
  }
  return { data: [] };
};

export const trimAndSplitJodi = (passedValue) => {
  try {
    const splitVal = passedValue.trim().split(" ");
    return splitVal.length > 1 ? splitVal[1] : splitVal[0];
  } catch {
    return passedValue.trim();
  }
};

export const validatePhoneNumber = (phoneNumber: string) => {
  let errors = {};
  let isValid = true;
  // if (!input["Mobilenumber"]) {
  //     isValid = false;
  //     errors["Mobilenumber"] = "Please enter your Mobile Number.";
  // }
  if (phoneNumber.length === 0) {
    errors["mobilenumber"] = "";
    return {
      isValid,
      errors,
    };
  }
  if (typeof phoneNumber !== "undefined") {
    const pattern = new RegExp(
      /^([+]?[\s0-9]+)?(\d{3}|[(]?[0-9]+[)])?([-]?[\s]?[0-9])+$/i
    );
    if (!pattern.test(phoneNumber)) {
      isValid = false;
      errors["mobilenumber"] = "Please Enter Number Only";
    } else if (phoneNumber.length < 10) {
      isValid = false;
      errors["mobilenumber"] = "Enter at least 10 digits mobile number.";
    } else if (phoneNumber.length > 10) {
      isValid = false;
      errors["mobilenumber"] = "Mobile number should be maximum of 10 digits.";
    }
  }

  return {
    isValid,
    errors,
  };
};

export const validatePANnumber = (panVal: string) => {
  const regpan = new RegExp(/^([a-zA-Z]){5}([0-9]){4}([a-zA-Z]){1}?$/);

  if (panVal.length === 0) {
    return {
      error: "",
    };
  }
  if (regpan.test(panVal)) {
    return {
      error: "",
    };
  } else {
    // invalid pan card number
    return {
      error: "Please enter valid PAN number.",
    };
  }
};

export const sumOfDigits = (num: number) => {
  let sum = 0;

  while (num) {
    sum += num % 10;
    num = Math.floor(num / 10);
  }
  return sum;
};
export const getLastDigitOfANumber = (num: number) => {
  return Number.isInteger(num) ? num % 10 : num.toString().slice(-1);
};
export const getCentralMatkaNumber = (lhsVal = 0, rhsVal = 0) => {
  const fistDigit =
    !isNaN(lhsVal) && lhsVal > 0
      ? getLastDigitOfANumber(sumOfDigits(lhsVal)).toString()
      : "*";
  const secondDigit =
    !isNaN(rhsVal) && rhsVal > 0
      ? getLastDigitOfANumber(sumOfDigits(rhsVal)).toString()
      : "*";

  return `${fistDigit}${secondDigit}`;
};
const sortableTime = (item: any) =>
  item?.gamesTimeTableUI?.startTime || item?.startTime || item?.openTime || "00:00";

export const compareTimes = (obj1: any, obj2: any) => {
  try {
    const [hours1, minutes1] = sortableTime(obj1).split(":").map(Number);
    const [hours2, minutes2] = sortableTime(obj2).split(":").map(Number);

    if (hours1 !== hours2) {
      return hours1 - hours2;
    }
    return minutes1 - minutes2;
  } catch (e) {
    console.error("compareTimes error", e);
    return 0;
  }
};
export const sortListByHHMMTime = (timeArray: any) => {
  return timeArray.sort(compareTimes);
};
//locale storage
export const saveItemInLocalStorage = (key: string, data: string) => {
  localStorage.setItem(key, data);
};

export const getItemFromLocalStorage = (key: string) => {
  try {
    return localStorage.getItem(key);
  } catch (e) {
    console.error("Local storage item not found");
    return undefined;
  }
};

export const removeItemFromLocalStorage = (key: string) => {
  try {
    localStorage.removeItem(key);
  } catch (e) {
    console.error("Local storage item not found for remove");
  }
};

//
export const getWeekDaysInMonth = () => {
  function getWeeksInMonth() {
    const currentDate = new Date();
    const currentMonth = currentDate.getMonth();
    const currentYear = currentDate.getFullYear();

    const firstDayOfMonth = new Date(currentYear, currentMonth, 1);
    const lastDayOfMonth = new Date(currentYear, currentMonth + 1, 0);

    const firstMonday =
      firstDayOfMonth.getDate() + ((1 - firstDayOfMonth.getDay() + 7) % 7);
    const lastSunday =
      lastDayOfMonth.getDate() - ((lastDayOfMonth.getDay() + 6) % 7);

    const weeks: any = [];
    let currentDay = new Date(currentYear, currentMonth, firstMonday);

    while (currentDay.getDate() <= lastSunday) {
      const startOfWeek = new Date(currentDay);
      const endOfWeek = new Date(currentDay.setDate(currentDay.getDate() + 6));

      weeks.push({ startOfWeek, endOfWeek });

      currentDay.setDate(currentDay.getDate() + 1);
    }

    return weeks;
  }

  // Usage:
  const weeksInMonth = getWeeksInMonth();
  console.log(weeksInMonth);
};
export const getWeeklyData = (data: any[]) => {
  const sortedData = data.sort(
    (a: any, b: any) =>
      new Date(a.createdTime.replace("Z", "")).valueOf() -
      new Date(b.createdTime.replace("Z", "")).valueOf()
  );
  let weeklyData: any = [];

  let startOfWeek: Date | null = null;
  let endOfWeek: Date | null = null;
  let currentWeekData = {};

  for (const entry of sortedData) {
    const currentDate = new Date(entry.createdTime.replace("Z", ""));

    if (!startOfWeek) {
      // First entry
      let startOfWeek = new Date(currentDate);
      startOfWeek.setDate(startOfWeek.getDate() - startOfWeek.getDay() + 1);
      let endOfWeek: Date = new Date(startOfWeek);
      endOfWeek.setDate(endOfWeek.getDate() + 6);
      let currentWeekData: any = {};
      currentWeekData[currentDate.getDay()] = [entry];
    } else if (
      (currentDate as Date) >= (startOfWeek as Date) &&
      endOfWeek !== null &&
      currentDate <= (endOfWeek as Date)
    ) {
      // Date is within the current week
      const dayOfWeek = currentDate.getDay();
      if (!currentWeekData[dayOfWeek]) {
        currentWeekData[dayOfWeek] = [entry];
      } else {
        currentWeekData[dayOfWeek].push(entry);
      }
    } else {
      // Date is in a new week
      weeklyData.push(currentWeekData);
      if (startOfWeek !== null && endOfWeek !== null) {
        (startOfWeek as Date).setDate((startOfWeek as Date).getDate() + 7);
        (endOfWeek as Date).setDate((endOfWeek as Date).getDate() + 7);
      }
      currentWeekData = {};
      currentWeekData[currentDate.getDay()] = [entry];
    }
  }

  // Add the last week's data
  weeklyData.push(currentWeekData);
  return weeklyData;
};

export const getDataByGameName = (data: any[], filterKey: string) => {
  return data.filter((item) => item.name === filterKey);
};

export const parseWeekRange = (range: string) => {
  const [start, end] = range.split(" to "); // Split into start and end dates
  const startDate = new Date(
    `20${start.split("/")[2]}-${start.split("/")[1]}-${start.split("/")[0]}`
  );
  const endDate = new Date(
    `20${end.split("/")[2]}-${end.split("/")[1]}-${end.split("/")[0]}`
  );
  return { startDate, endDate };
};
const IST_WEEKDAY_INDEX: Record<string, number> = {
  Mon: 0,
  Tue: 1,
  Wed: 2,
  Thu: 3,
  Fri: 4,
  Sat: 5,
  Sun: 6,
};

export const getIstWeekdayIndex = (input: Date | string) => {
  const date = input instanceof Date ? input : new Date(input);
  if (Number.isNaN(date.getTime())) {
    return 0;
  }
  const weekday = new Intl.DateTimeFormat("en-US", {
    timeZone: IST_TIME_ZONE,
    weekday: "short",
  }).format(date);
  return IST_WEEKDAY_INDEX[weekday] ?? 0;
};

const formatIstKeyAsChartDate = (dateKey: string) => {
  const [year, month, day] = dateKey.split("-");
  return `${day}/${month}/${year.slice(2)}`;
};

export const getWeekRangeVal = (obj: GameObject) => {
  const dateKey = getIstDateKey(obj.createdTime);
  const [year, month, day] = dateKey.split("-").map(Number);
  const noonIst = new Date(Date.UTC(year, month - 1, day, 6, 30));
  const start = new Date(noonIst);
  start.setUTCDate(start.getUTCDate() - getIstWeekdayIndex(noonIst));
  const end = new Date(start);
  end.setUTCDate(end.getUTCDate() + 6);
  return `${formatIstKeyAsChartDate(getIstDateKey(start))} to ${formatIstKeyAsChartDate(getIstDateKey(end))}`;
};
// get the list weekRange
export const getWeekwisedata = (data: GameObject[]) => {
  const segregatedData: { [week: string]: GameObject[] } = {};
  data.map(withGameUI).forEach((obj) => {
    const weekRange = getWeekRangeVal(obj);
    const dayIndex = getIstWeekdayIndex(obj.createdTime);
    const emptyDay = {
      rhsValue: "***",
      lhsValue: "***",
      name: obj.gamesTimeTableUI?.name || obj.name,
      createdTime: obj.createdTime,
      startTime: obj.startTime,
      endTime: obj.endTime,
      gamesTimeTableUI: obj.gamesTimeTableUI,
      winnerValue: "**",
    };

    if (!segregatedData[weekRange]) {
      segregatedData[weekRange] = Array.from({ length: 7 }, () => ({
        ...emptyDay,
      }));
    }
    segregatedData[weekRange][dayIndex] = obj;
  });

  return segregatedData;
};

export const getPanelJodiChartDetailData = (
  data: any[],
  filterKey: string
) => {
  try {
    const dataByGameName = getPanelJodiChartData(data)[filterKey];
    if (dataByGameName) {
      const sortedData = dataByGameName.sort(
        (a: any, b: any) =>
          new Date(a.createdTime).valueOf() - new Date(b.createdTime).valueOf()
      );
      const weekwiseRangedata = getWeekwisedata(sortedData);
      const mappedData = Object.keys(weekwiseRangedata).map((weekRange) => {
        return {
          weekIntervalTime: weekRange,
          dayWiseData: weekwiseRangedata[weekRange],
        };
      });
      // console.log("getPanelJodiChartDetailData step4:", { mappedData });
      return mappedData;
    }
  } catch (e) {
    console.error("getPanelJodiChartDetailData error", e);
    return [];
  }
};

export const getPanelJodiChartData = (data: any[]) => {
  const segregatedData = {};
  data.forEach((obj) => {
    const gameName = gameDisplayName(obj);

    if (segregatedData[gameName]) {
      segregatedData[gameName].push(obj);
    } else {
      segregatedData[gameName] = [obj];
    }
  });
  return segregatedData;
};

export const getFatafatResultsDetailData = (data: any[], desiredLength = 8) => {
  if (!data) {
    return [];
  }
  const sortedData = [...data].sort(
    (a: any, b: any) =>
      new Date(a.createdTime).valueOf() - new Date(b.createdTime).valueOf()
  );

  const segregatedData = sortedData.reduce((result, item) => {
    const dateKey = getIstDateKey(item?.createdTime);

    if (!isEmpty(dateKey)) {
      if (!result[dateKey]) {
        result[dateKey] = [];
      }
      result[dateKey].push(item);
    }
    return result;
  }, {});

  return Object.keys(segregatedData).map((item) => {
    // check for empty days
    let dayList = segregatedData[item];
    const currentLength = dayList.length;

    if (currentLength < desiredLength) {
      const numberOfItemsToAdd = desiredLength - currentLength;
      for (let i = 0; i < numberOfItemsToAdd; i++) {
        dayList.push({
          guessNumber: "***",
          createdTime: segregatedData[item][0].createdTime,
          startTime: segregatedData[item][0].startTime,
          winnerValue: "**",
        });
      }
    } else if (currentLength > desiredLength) {
      dayList.splice(desiredLength);
    }
    return {
      date: item,
      dateWiseData: segregatedData[item],
    };
  });
};

export const getAddUpdateGameInitialState = (data: any[], index = null) => {
  const initialState = {
    gameName: "",
    startTime: "",
    endTime: "",
    lhsValue: "",
    rhsValue: "",
    winnerValue: "",
    createdTime: "",
    guessGameTypeId: -1,
  };

  try {
    if (!isEmpty(index) && index != null) {
      // const foundObj = sortListByHHMMTime(data)[index];
      const foundObj = data[index];
      // const foundObj = data[parseInt(index)];

      return {
        ...initialState,
        gameName: foundObj?.name,
        startTime: foundObj?.startTime,
        endTime: foundObj?.endTime,
        lhsValue: foundObj?.lhsValue,
        rhsValue: foundObj?.rhsValue,
        winnerValue: foundObj?.winnerValue,
        createdTime: foundObj?.createdTime,
        guessGameTypeId: foundObj?.guessGameTypeId,
      };
    }
  } catch (e) {
    return initialState;
  }
  return initialState;
};
export const getGuessTableAddEditInitialState = (
  data: any[],
  guessGameId = null
) => {
  const initialState = {
    gameName: "",
    guessNumber: "",
    winnerValue: "",
    createdTime: "",
    guessGameId: null,
  };

  try {
    const foundIndex = data.findIndex(
      (ele) => ele.guessGameId === parseInt(guessGameId ?? "-001")
    );
    if (foundIndex > -1) {
      const foundObj = data[foundIndex];

      return {
        ...initialState,
        gameName: foundObj.gameName,
        guessNumber: foundObj.guessNumber,
        winnerValue: foundObj?.winnerValue,
        createdTime: foundObj?.createdTime,
        guessGameId: foundObj?.guessGameId,
      };
    }
  } catch (e) {
    return initialState;
  }
  return initialState;
};
export const getFatafatTableAddEditInitialState = (
  data: any[],
  fatafatGuessId = null
) => {
  const initialState = {
    guessNumber: "",
    winnerValue: "",
    createdTime: "",
    startTime: "",
    fatafatGuessId: null,
  };

  try {
    const foundIndex = data.findIndex(
      (ele) => ele.fatafatGuessId === parseInt(fatafatGuessId ?? "-001")
    );
    if (foundIndex > -1) {
      const foundObj = data[foundIndex];

      return {
        ...initialState,
        guessNumber: foundObj.guessNumber,
        winnerValue: foundObj?.winnerValue,
        createdTime: foundObj?.createdTime,
        fatafatGuessId: foundObj?.fatafatGuessId,
        startTime: foundObj?.startTime,
      };
    }
  } catch (e) {
    return initialState;
  }
  return initialState;
};

const matchesIstDate = (item: any, dateKey: string) =>
  Boolean(item?.createdTime) && getIstDateKey(item.createdTime) === dateKey;

/**
 * Games whose createdTime falls on today's IST date.
 * Before 09:00 IST, yesterday's set is shown when today does not exist yet.
 */
export const getCurrentDayGames = (data: any[]) => {
  try {
    const list = Array.isArray(data) ? data : [];
    const todayKey = getIstDateKey(new Date());
    const currentdayGames = list
      .filter((item) => matchesIstDate(item, todayKey))
      .map(withGameUI)
      .sort(compareTimes);
    if (currentdayGames.length > 0) {
      return currentdayGames;
    }
    if (!isDailyResetReached()) {
      const yesterdayKey = shiftIstDateKey(todayKey, -1);
      return list
        .filter((item) => matchesIstDate(item, yesterdayKey))
        .map(withGameUI)
        .sort(compareTimes);
    }
    return [];
  } catch (e) {
    console.log("getCurrentDayGames error", e);
    return [];
  }
};

/** Every stored game except the set currently on screen. Compares the full IST date, not the day-of-month. */
export const getPastDaysGames = (data: any[]) => {
  const list = Array.isArray(data) ? data : [];
  const visible = getCurrentDayGames(list);
  const visibleIds = new Set(visible.map(gameIdentity));
  if (visibleIds.size === 0) {
    return list;
  }
  return list.filter((item) => !visibleIds.has(gameIdentity(item)));
};

export const getYesrdaysDaysGames = (data: any[]) => {
  const yesterdayKey = shiftIstDateKey(getIstDateKey(new Date()), -1);
  return (data || []).filter((item) => matchesIstDate(item, yesterdayKey));
};

export const updateGameAtCurrentIndex = (
  allGames: any[],
  index: number,
  nextGame: any
) => {
  const current = getCurrentDayGames(allGames || []);
  const target = current[index];
  if (!target) {
    return [...(allGames || []), withGameUI(nextGame)];
  }
  const targetId = gameIdentity(target);
  let replaced = false;
  const next = (allGames || []).map((game) => {
    if (!replaced && gameIdentity(game) === targetId) {
      replaced = true;
      return withGameUI({
        ...game,
        ...nextGame,
        createdTime: game.createdTime,
      });
    }
    return game;
  });
  return replaced ? next : [...next, withGameUI(nextGame)];
};

export const upsertTodayGame = (allGames: any[], nextGame: any) => {
  const createdTime = nextGame?.createdTime || new Date().toISOString();
  const todayKey = getIstDateKey(createdTime);
  const name = nextGame?.name || nextGame?.gamesTimeTableUI?.name;
  const index = (allGames || []).findIndex(
    (game) =>
      matchesIstDate(game, todayKey) && gameDisplayName(game) === name
  );
  if (index >= 0) {
    const copy = [...allGames];
    copy[index] = withGameUI({
      ...copy[index],
      ...nextGame,
      createdTime: copy[index].createdTime || createdTime,
    });
    return copy;
  }
  return [...(allGames || []), withGameUI({ ...nextGame, createdTime })];
};

export const buildEmptyGame = (item: any, index: number, createdTime: string) => {
  const name = (item?.name || item?.gamesTimeTableUI?.name || "").trim();
  const startTime = item?.startTime || item?.openTime || item?.gamesTimeTableUI?.startTime || "";
  const endTime = item?.endTime || item?.closeTime || item?.gamesTimeTableUI?.endTime || "";
  const gamesTimeTableId = item?.gamesTimeTableId ?? index + 1;
  return withGameUI({
    name,
    startTime,
    endTime,
    gamesTimeTableId,
    guessGameTypeId: gamesTimeTableId,
    lhsValue: "***",
    rhsValue: "***",
    winnerValue: "**",
    createdTime,
  });
};

export type DailyGamePlan = {
  action: "wait" | "skip" | "mark" | "create";
  todayKey: string;
  gamesToAdd: any[];
};

const marketName = (item: any) =>
  (item?.gamesTimeTableUI?.name || item?.name || "").trim().toLowerCase();

/**
 * One automatic empty set per IST date, and only from 09:00 IST onward.
 * If today's rows already exist, refresh does not write game results again.
 * After the first create, later hours and other devices do nothing.
 * Admin edits stay in place. Extra markets are added only from the admin screen.
 */
export const planDailyGames = (data: any, now: Date = new Date()): DailyGamePlan => {
  const todayKey = getIstDateKey(now);
  const todayCount = (Array.isArray(data?.gameTypes) ? data.gameTypes : []).filter((game) =>
    matchesIstDate(game, todayKey)
  ).length;
  if (data?.lastDailyGamesCreatedOn === todayKey || todayCount > 0) {
    return { action: "skip", todayKey, gamesToAdd: [] };
  }
  if (!isDailyResetReached(now)) {
    return { action: "wait", todayKey, gamesToAdd: [] };
  }

  const timetable = sortListByHHMMTime([...(data?.gamesTimeTable || [])]);
  const seen = new Set(
    (Array.isArray(data?.gameTypes) ? data.gameTypes : [])
      .filter((game) => matchesIstDate(game, todayKey))
      .map(marketName)
      .filter(Boolean)
  );
  const gamesToAdd: any[] = [];
  timetable.forEach((item, index) => {
    const name = (item?.name || item?.gamesTimeTableUI?.name || "").trim();
    const key = name.toLowerCase();
    if (!key || seen.has(key)) {
      return;
    }
    seen.add(key);
    gamesToAdd.push(buildEmptyGame({ ...item, name }, index, now.toISOString()));
  });

  if (gamesToAdd.length === 0) {
    return { action: "mark", todayKey, gamesToAdd: [] };
  }
  return { action: "create", todayKey, gamesToAdd };
};

/**
 * API daily copy is disabled. Empty games are appended in Firebase and existing
 * results for the same IST date are left in place.
 */
export const createGamesForTodayAPICall = (_gamesList: any, callback) => {
  // APIService.post(API_CONFIGS.CREATE_GAME.URL, obj)
  console.info("createGamesForTodayAPICall skipped; Firebase daily create is used.");
  if (callback) {
    callback(false);
  }
};

export const createGamesForCurrentDay = (data: any, callback) => {
  const preview = planDailyGames(data);
  if (preview.action !== "create" || preview.gamesToAdd.length === 0) {
    callback(false);
    return;
  }

  const existing = Array.isArray(data?.gameTypes) ? data.gameTypes : [];
  updateCollection(FIREBASE_COLLECTION_KEY, FIREBASE_COLLECTION_DOC_KEY, {
    gameTypes: [...existing, ...preview.gamesToAdd],
    lastDailyGamesCreatedOn: preview.todayKey,
  })
    .then(() => {
      console.info("createGamesForCurrentDay created", {
        todayKey: preview.todayKey,
        added: preview.gamesToAdd.length,
      });
      callback(true);
    })
    .catch((e) => {
      console.error("firebase updated failed createGamesForCurrentDay!", e);
      callback(false);
    });
};

// const removeDuplicate = (arrayVal) => {
//   let uniqueTime = [];
//   let uniqueArrays = [];
//   arrayVal.forEach(element => {
//     if (!uniqueTime.includes(element.name)) {
//       uniqueTime.push(element.name);
//       uniqueArrays.push(element);
//     }
//   });
//   return uniqueArrays;
// };
